import type { SyntheticEvent } from 'react'
import { getElementByRemoteId } from '../sandbox/utils'
import { SerializedElement, SerializedEvent, SerializedType, SerializedValue } from '../types'
import { COMP_ID_DATA_ATTR } from './consts'
import { deepMap } from './helpers'

const UNUSED_ELEMENTS_CACHE_CLEANUP_INTERVAL = 10 * 1000
const CACHE_ENTRY_TIME_TO_LIVE = 10 * 1000

const elementMap = new Map<string, Element>()
const lastAccessTimeMap = new Map<string, number>()

const lastUnusedElementsCleanupTime = Date.now()

export const serializeEvent = (event: Event | SyntheticEvent): SerializedEvent => {
	return Object.entries(event).reduce(
		(acc, [key, value]) => {
			if (key.startsWith('_')) {
				// Private SyntheticEvent properties can be ignored
				return acc
			}

			if (key === 'nativeEvent') {
				const nativeEventProperties: any = {}
				for (const prop in value) {
					nativeEventProperties[prop] = value[prop]
				}

				const serializedNativeEvent = serializeEvent(nativeEventProperties)

				return {
					...acc,
					[key]: {
						...serializedNativeEvent,
						$type$: SerializedType.NATIVE_EVENT,
					},
				}
			}

			if (value instanceof Window) {
				return { ...acc, [key]: { $type$: SerializedType.WINDOW } }
			}

			if (value instanceof Document) {
				return { ...acc, [key]: { $type$: SerializedType.DOCUMENT } }
			}

			if (value instanceof Element) {
				const remoteId = value.getAttribute(COMP_ID_DATA_ATTR)
				if (remoteId) {
					return {
						...acc,
						[key]: {
							$type$: SerializedType.ELEMENT,
							remoteId,
							value: (value as any).value,
							checked: (value as any).checked,
						} as SerializedElement,
					}
				} else {
					// Element is missing remoteId. This can happen when:
					// 1. Element was not created via remote-ui reconciler (e.g. when created via document.createElement).
					// 2. The element is "relatedTarget" and it is referencing an element outside of our renderer container.
					return acc
				}
			}

			if (value instanceof Event) {
				return serializeEvent(value)
			}

			if (typeof value === 'function') {
				// remote-ui will handle sending functions via `postMessage`, nothing to do here
				return { ...acc, [key]: value }
			}

			// We might encounter a value which cannot be serialized. This will throw an error during `postMessage` call.
			// In most cases it is better to remove such value as it is most likely not that important and it's better to omit it than fail entirely.
			let serializableValue: unknown
			try {
				serializableValue = value === undefined ? value : JSON.parse(JSON.stringify(value))
			} catch (e) {
				console.error(`Unserializable event value detected: ${key}=`, value)
			}

			return { ...acc, [key]: serializableValue }
		},
		{ $type$: SerializedType.EVENT } as SerializedEvent
	)
}

export const unserializeEvent = (serializedEvent: SerializedEvent) => {
	const proxies: { [remoteId: string]: Element } = {}

	return Object.entries(serializedEvent).reduce((acc, [key, value]) => {
		if (isSerializedProperty(value)) {
			if (value.$type$ === SerializedType.WINDOW) {
				acc[key] = window
			} else if (value.$type$ === SerializedType.DOCUMENT) {
				acc[key] = document
			} else if (value.$type$ === SerializedType.NATIVE_EVENT) {
				acc[key] = unserializeEvent(value as SerializedEvent)
			} else if (isSerializedElement(value) && value.remoteId) {
				Object.defineProperty(acc, key, {
					enumerable: true,
					configurable: true,
					get() {
						const { remoteId } = value
						const cacheEntry = elementMap.get(remoteId)
						const element = cacheEntry || getElementByRemoteId(remoteId)

						if (!element) {
							return null
						}

						if (!cacheEntry) {
							elementMap.set(remoteId, element)
						}

						lastAccessTimeMap.set(remoteId, Date.now())

						runGarbageCollectionIfNeeded()

						if (remoteId in proxies) {
							return proxies[remoteId]
						}

						return (proxies[remoteId] = new Proxy(element, {
							get(target, prop) {
								if (prop === 'value') {
									return value.value
								}

								if (prop === 'checked') {
									return value.checked
								}

								return target[prop as keyof Element]
							},
						}))
					},
				})
			}
		} else if (key !== '$type$') {
			acc[key] = value
		}

		return acc
	}, {} as Record<string, any>)
}

const runGarbageCollectionIfNeeded = () => {
	if (Date.now() - lastUnusedElementsCleanupTime > UNUSED_ELEMENTS_CACHE_CLEANUP_INTERVAL) {
		lastAccessTimeMap.forEach((lastAccessTime, elementId, map) => {
			if (Date.now() - lastAccessTime > CACHE_ENTRY_TIME_TO_LIVE) {
				map.delete(elementId)
			}
		})
	}
}

export const deepFnSerialize = (props: Record<string, any>) => {
	return deepMap(props, (val: any) => {
		if (typeof val === 'function') {
			const wrappedFunction = (...args: Array<any>) => {
				const argsWithSerializedEvents = (args || []).map((arg) => {
					const isDomEvent = typeof arg === 'object' && arg.target
					if (isDomEvent) {
						return serializeEvent(arg)
					}

					return arg
				})

				return val(...argsWithSerializedEvents)
			}

			return wrappedFunction
		}

		return val
	})
}

export const deepFnUnserialize = (props: Record<string, any>) => {
	return deepMap(props, (value: any) => {
		if (typeof value === 'function') {
			const wrappedFunction = (...args: Array<any>) => {
				const argsWithSerializedEvents = (args || []).map((arg) => {
					if (isSerializedEvent(arg)) {
						return unserializeEvent(arg)
					}

					return arg
				})

				return value(...argsWithSerializedEvents)
			}

			return wrappedFunction
		}

		return value
	})
}

export const isSerializedEvent = (value: any): value is SerializedEvent =>
	!!value && value.$type$ === SerializedType.EVENT

export const isSerializedElement = (value: any): value is SerializedElement =>
	!!value && value.$type$ === SerializedType.ELEMENT

const isSerializedProperty = (value: any): value is SerializedValue => !!value && value.$type$
